import type { ReactElement, ReactNode } from "react";
import type {
  ProblemTimelineParams,
  TimelineMilestoneKind,
} from "@playbookai/widget-schemas";
import { SortableList } from "../../molecules/SortableList/SortableList.js";
import { AddButton } from "./AddButton.js";
import { MilestoneRow } from "./MilestoneRow.js";

/** Editable manual order; the separate canvas track remains chronological. */
export function MilestoneList({
  data,
  readOnly,
  onAdd,
  selectedId,
  onSelect,
  onReorder,
}: {
  readonly data: ProblemTimelineParams;
  readonly readOnly: boolean;
  readonly onAdd: (kind: TimelineMilestoneKind) => void;
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string) => void;
  readonly onReorder: (orderedIds: string[]) => void;
}): ReactElement {
  const milestones = data.milestones;
  const hasKind = (kind: TimelineMilestoneKind): boolean =>
    milestones.some((milestone) => milestone.kind === kind);
  const rows = milestones.map((milestone) => (
    <MilestoneRow
      key={milestone.id}
      milestone={milestone}
      readOnly={readOnly}
      selected={milestone.id === selectedId}
      onSelect={() => onSelect(milestone.id)}
    />
  ));

  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-lg border border-pb-border bg-pb-card-elevated/30">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-pb-border px-3 py-2">
        <h3 className="text-sm font-medium text-pb-text-secondary">
          Milestones ({milestones.length})
        </h3>
        {!readOnly ? (
          <div className="flex flex-wrap justify-end gap-1.5">
            {!hasKind("occurred") ? (
              <AddButton onClick={() => onAdd("occurred")}>
                Add occurred
              </AddButton>
            ) : null}
            {!hasKind("recognised") ? (
              <AddButton onClick={() => onAdd("recognised")}>
                Add recognised
              </AddButton>
            ) : null}
            <AddButton onClick={() => onAdd("custom")}>Add milestone</AddButton>
          </div>
        ) : null}
      </header>
      {milestones.length > 0 && (
        <div
          aria-hidden="true"
          className={`hidden grid-cols-[4.5rem_minmax(0,1fr)_5rem] gap-3 border-b border-pb-border py-2 pe-4 text-xs text-pb-text-muted sm:grid ${readOnly ? "ps-4" : "ps-10"}`}
        >
          <span>Date</span>
          <span>Event</span>
          <span>Confidence</span>
        </div>
      )}
      {milestones.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs italic text-pb-text-muted">
          No milestones yet. Start with the last-known-good or occurrence date,
          then when the problem was first recognised.
        </p>
      ) : readOnly ? (
        <MilestoneRows>{rows}</MilestoneRows>
      ) : (
        <SortableList
          ids={milestones.map((milestone) => milestone.id)}
          onReorder={onReorder}
        >
          <MilestoneRows>{rows}</MilestoneRows>
        </SortableList>
      )}
    </section>
  );
}

function MilestoneRows({ children }: { children: ReactNode }): ReactElement {
  return (
    <ul
      aria-label="Milestones"
      className="min-h-0 max-h-96 flex-1 divide-y divide-pb-border overflow-y-auto"
    >
      {children}
    </ul>
  );
}
